import { useCallback, useEffect, useMemo, useState } from "react";
import { Tag } from "../../../../../models/common/tag.model";
import {
  AddTagSection,
  ButtonContainer,
  ButtonSection,
  ColumnTagList,
  Form,
  InputColor,
  LabelNewTag,
  LabelNewTagBold,
  LoadingSpinner,
  ModalTitle,
  OpenModalAdd,
  TagList,
  TagsContainer,
} from "./index.styled";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableTag from "./components/draggable-tag";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../common/button/button";
import { useTheme } from "styled-components";
import Modal from "../../../../modal/modal";
import SelectTagColorful from "../../../../form/select-tag-colorful";
import { useDispatch, useSelector } from "react-redux";
import {
  addCandidateTag,
  createTag,
  fetchTags,
  removeCandidateTag,
} from "../../../../../store/candidate/candidate-actions";
import { action } from "typesafe-actions";
import { CandidateActionTypes } from "../../../../../store/candidate/candidate-types";
import { AppState } from "../../../../../store/app-state";
import Input from "../../../../form/Input/input";
import { SectionBlock, Title } from "../../../../talent/talent.styled";
import useUmamiTracking from "../../../../../hooks/use-umami-tracking";
import { Events } from "../../../../../utils/constants";

export interface TagsProps {
  tags: Tag[];
  candidateId: string;
  tagsChanged: () => void;
  layout: 'table' | 'talent';
  id?: string;
}

interface TagOption {
  value: string;
  label: string;
  color: string;
}

const modalTalent = document.getElementById("modal-talent");

const Tags: React.FC<TagsProps> = ({ tags, candidateId, tagsChanged, layout, id }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isModalColorOpen, setModalColorOpen] = useState(false);
  const [newTags, setNewTags] = useState<TagOption[]>([]);
  const { t } = useTranslation("private-home-page");
  const theme = useTheme();
  const dispatch = useDispatch();
  const pushUmami = useUmamiTracking();
  const user = useSelector((state: AppState) => state.authState.user);
  const { data: tagOptions } = useSelector(
    (state: AppState) => state.candidateState.tagsData
  );
  const { loading: loadingAddCandidate } = useSelector(
    (state: AppState) => state.candidateState.addCandidateTagData
  );
  const { loading: loadingRemoveCandidate } = useSelector(
    (state: AppState) => state.candidateState.removeCandidateTagData
  );
  const { data: createdTag } = useSelector(
    (state: AppState) => state.candidateState.createTagData
  );
  const [color, setColor] = useState("#034872");
  const [name, setName] = useState("");
  const [startLoadingAddTag, setStartLoadingAddTag] = useState(false);
  const [startLoadingRemoveTag, setStartLoadingRemoveTag] = useState(false);

  const handleRemoveTag = useCallback(
    (tag: Tag) => {
      dispatch(removeCandidateTag(tag.id, candidateId));
      setStartLoadingRemoveTag(true);
      pushUmami(
        Events.REMOVE_TAG, 
        {tag: tag.name, candidate_id: candidateId}, 
        user
      );
    },
    [dispatch, candidateId, setStartLoadingRemoveTag, pushUmami, user]
  );

  useEffect(() => {
    if (!loadingAddCandidate && startLoadingAddTag) {
      setModalOpen(false);
      setStartLoadingAddTag(false);
      tagsChanged();
    }
    if (!loadingRemoveCandidate && startLoadingRemoveTag) {
      setStartLoadingRemoveTag(false);
      tagsChanged();
    }
  }, [
    dispatch,
    loadingAddCandidate,
    loadingRemoveCandidate,
    tagsChanged,
    startLoadingAddTag,
    setStartLoadingAddTag,
    startLoadingRemoveTag,
  ]);

  useEffect(() => {
    if (isModalOpen) {
      setNewTags(
        tags.map((tag) => ({
          value: tag.id.toString(),
          label: tag.name,
          color: tag.color,
        }))
      );
    }
  }, [tags, isModalOpen]);

  useEffect(() => {
    if (!isModalColorOpen) {
      setName("");
      setColor("#034872");
    }
  }, [isModalColorOpen, setName, setColor]);

  useEffect(() => {
    if (!isModalOpen) {
      setNewTags([]);
    }
  }, [isModalOpen, setNewTags]);

  const moveTag = useCallback((fromIndex: number, toIndex: number) => {
    const newTags = [...tags];
    const [movedTag] = newTags.splice(fromIndex, 1);
    newTags.splice(toIndex, 0, movedTag);
  }, [tags]);

  const handleSearchTag = useCallback(
    (input: string) => {
      if (input.length) {
        dispatch(fetchTags(input));
      } else {
        dispatch(action(CandidateActionTypes.FETCH_TAGS_SUCCESS, []));
      }
    },
    [dispatch]
  );

  const handleAddTag = useCallback(() => {
    const ids = newTags.map((tag) => tag.value);
    dispatch(addCandidateTag(ids, candidateId));
    setStartLoadingAddTag(true);

    pushUmami(
      Events.ADD_TAG, 
      {tags: newTags.map(t => t.label), candidate_id: candidateId}, 
      user
    );
  }, [dispatch, newTags, candidateId, setStartLoadingAddTag, pushUmami, user]);

  const getTagOptions = useMemo(() => {
    return tagOptions?.map(
      (tag) =>
        ({
          value: tag.id.toString(),
          label: tag.name,
          color: tag.color,
        } as TagOption)
    );
  }, [tagOptions]);

  useEffect(() => {
    if(createdTag) {
      const createdTagOption: TagOption = {
        value: createdTag.id.toString(),
        label: createdTag.name,
        color: createdTag.color,
      }
      setNewTags((prev) => [...prev, createdTagOption]);
      dispatch(action(CandidateActionTypes.CREATE_TAG_SUCCESS, null));
    }
  }, [createdTag, dispatch]);

  const TableElement = useMemo(() => {
    return (
      <DndProvider backend={HTML5Backend}>
        <TagsContainer id={id}>
          <ButtonContainer>
            <OpenModalAdd onClick={() => setModalOpen(true)} />
          </ButtonContainer>
          <TagList>
            {tags.map((tag, index) => (
              <DraggableTag
                key={tag.id}
                tag={tag}
                index={index}
                moveTag={moveTag}
                handleRemoveTag={handleRemoveTag}
              />
            ))}
          </TagList>
        </TagsContainer>
      </DndProvider>
    )
  }, [handleRemoveTag, moveTag, tags, id]);

  const TalentElement = useMemo(() => {
    return (
      <DndProvider backend={HTML5Backend}>
        <SectionBlock id={id}>
          <ButtonContainer>
            <Title>Tags</Title>
            <OpenModalAdd onClick={() => setModalOpen(true)} />
          </ButtonContainer>
          <ColumnTagList>
            {tags.map((tag, index) => (
              <DraggableTag
                key={tag.id}
                tag={tag}
                index={index}
                moveTag={moveTag}
                handleRemoveTag={handleRemoveTag}
                width="100%"
              />
            ))}
          </ColumnTagList>
        </SectionBlock>
      </DndProvider>
    )
  }, [handleRemoveTag, moveTag, tags, id])

  const render = useCallback(() => {
    switch(layout) {
      case 'talent':
        return TalentElement;
      case 'table':
        return TableElement;
    }
  }, [TableElement, TalentElement, layout]);

  return (
    <div>
      {render()}
      <Modal
        width={400}
        isShown={isModalOpen}
        toggle={() => {
          setModalOpen(false);
        }}
        target={modalTalent}
        background={"#fff"}
        zIndex={100}
      >
        <ModalTitle>{t("tag.title")}</ModalTitle>

        <AddTagSection>
          <SelectTagColorful
            placeholder={t("tag.placeholder")}
            values={[...newTags]}
            options={getTagOptions}
            onChange={(selectedValues) => {
              setNewTags(selectedValues);
            }}
            onInputChange={(input) => handleSearchTag(input)}
          />
        </AddTagSection>
        <LabelNewTag>{t("tag.not-find-tag")}</LabelNewTag>
        <LabelNewTagBold
          onClick={() => {
            setModalColorOpen(true);
          }}
        >
          {" "}
          {t("tag.create-tag")}
        </LabelNewTagBold>
        <ButtonSection>
          <Button
            textColor={theme.colors.primaryBlue.text}
            backgroundColor={theme.colors.primaryBlue.main}
            onClick={() => {
              handleAddTag();
            }}
            width="10rem"
          >
            {!loadingAddCandidate ? t("tag.btn") : <LoadingSpinner />}
          </Button>
        </ButtonSection>
      </Modal>

      <Modal
        width={280}
        isShown={isModalColorOpen}
        toggle={() => {
          setModalColorOpen(false);
        }}
        target={modalTalent}
        background={"#fff"}
        zIndex={100}
      >
        <ModalTitle>{t("tag.new-tag")}</ModalTitle>
        <Form 
          onSubmit={(e) => {
            e.preventDefault();
            dispatch(createTag(name, color));
            setModalColorOpen(false);
          }}
        >
          <Input
            name="search"
            type="text"
            placeholder={t("tag.placeholder-name")}
            value={name}
            debounceDelay={0}
            onDeboncedValueChange={(newValue) => setName(newValue ?? "")}
            className="input-search"
          />
          <InputColor
            id="tag-color"
            name="tag-color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
          <ButtonSection>
            <Button
              textColor={theme.colors.primaryBlue.text}
              backgroundColor={theme.colors.primaryBlue.main}
              onClick={() => {}}
              width="10rem"
            >
              {t("tag.btn")}
            </Button>
          </ButtonSection>
        </Form>
      </Modal>
    </div>
  );
};

export default Tags;
